import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import JSZip from 'jszip';
import { optimizeImage, getSupportedFormats } from '../../../services/imageService';
import { FiUploadCloud, FiDownload, FiTrash2 } from 'react-icons/fi';

const ImageOptimizer = () => {
  const [images, setImages] = useState([]);
  const [optimizedImages, setOptimizedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingImages, setLoadingImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({
    maxWidth: 1920,
    quality: 80,
    maintainAspectRatio: true,
    format: 'jpeg'
  });

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      const newImages = acceptedFiles
        .filter(file => file.type.startsWith('image/'))
        .map(file => ({
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
          type: file.type,
          id: Date.now() + Math.random().toString(36).substr(2, 9)
        }));

      if (newImages.length > 0) {
        setImages(prev => [...prev, ...newImages]);
        setOptimizedImages(prev => [...prev, ...Array(newImages.length).fill(null)]);
        setLoadingImages(prev => [...prev, ...Array(newImages.length).fill(false)]);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: true
  });

  const handleSettingsChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const optimizeImageHandler = async (index) => {
    const imageToOptimize = images[index];
    if (!imageToOptimize) return;

    setLoadingImages(prev => {
      const newLoadingImages = [...prev];
      newLoadingImages[index] = true;
      return newLoadingImages;
    });

    try {
      const result = await optimizeImage(
        imageToOptimize.file,
        settings.quality,
        settings.format,
        settings.maxWidth,
        settings.maintainAspectRatio
      );

      const optimizedImageData = {
        preview: result.preview,
        size: result.newSize,
        type: `image/${settings.format}`
      };

      setOptimizedImages(prev => {
        const newOptimizedImages = [...prev];
        newOptimizedImages[index] = optimizedImageData;
        return newOptimizedImages;
      });
    } catch (error) {
      console.error('Error al optimizar la imagen:', error);
      setError(`Error al optimizar la imagen: ${error.message}`);
    } finally {
      setLoadingImages(prev => {
        const newLoadingImages = [...prev];
        newLoadingImages[index] = false;
        return newLoadingImages;
      });
    }
  };

  const optimizeAllImages = async () => {
    setLoading(true);
    
    // Set all images to loading state
    setLoadingImages(prev => prev.map(() => true));
    
    try {
      const optimizationPromises = images.map((_, index) => {
        if (!optimizedImages[index]) {
          return optimizeImageHandler(index);
        }
        return Promise.resolve();
      });
      
      await Promise.all(optimizationPromises);
    } catch (error) {
      console.error('Error al optimizar las imágenes:', error);
      setError('Error al optimizar múltiples imágenes: ' + error.message);
    } finally {
      setLoading(false);
      
      // Reset all image loading states
      setLoadingImages(prev => prev.map(() => false));
    }
  };

  const handleDownload = (index) => {
    const optimizedImage = optimizedImages[index];
    const originalImage = images[index];
    if (optimizedImage && originalImage) {
      const extension = settings.format === 'webp' ? '.webp' : 
                       settings.format === 'png' ? '.png' : '.jpg';
      const link = document.createElement('a');
      link.href = optimizedImage.preview;
      link.download = `optimized_${originalImage.name.split('.')[0]}${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadAll = () => {
    optimizedImages.forEach((optimizedImage, index) => {
      if (optimizedImage) {
        handleDownload(index);
      }
    });
  };

  const handleDownloadAllAsZip = async () => {
    try {
      setLoading(true);
      const zip = new JSZip();
      
      // Create an array of promises to fetch all the optimized images
      const imagePromises = optimizedImages.map(async (optimizedImage, index) => {
        if (!optimizedImage) return null;
        
        const originalImage = images[index];
        const extension = settings.format === 'webp' ? '.webp' : 
                          settings.format === 'png' ? '.png' : '.jpg';
        const fileName = `optimized_${originalImage.name.split('.')[0]}${extension}`;
        
        try {
          // Fetch the blob from the optimized image URL
          const response = await fetch(optimizedImage.preview);
          const blob = await response.blob();
          return { fileName, blob };
        } catch (error) {
          console.error(`Error fetching image ${fileName}:`, error);
          return null;
        }
      });
      
      // Wait for all image fetches to complete
      const imageData = await Promise.all(imagePromises);
      
      // Add each successfully fetched image to the zip
      imageData.forEach(data => {
        if (data) {
          zip.file(data.fileName, data.blob);
        }
      });
      
      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create a download link for the zip file
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `optimized_images_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error creating zip file:', error);
      setError(`Error al crear el archivo zip: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setImages(prev => {
      const newImages = [...prev];
      newImages.splice(index, 1);
      return newImages;
    });
    
    setOptimizedImages(prev => {
      const newOptimizedImages = [...prev];
      newOptimizedImages.splice(index, 1);
      return newOptimizedImages;
    });
    
    setLoadingImages(prev => {
      const newLoadingImages = [...prev];
      newLoadingImages.splice(index, 1);
      return newLoadingImages;
    });
  };

  const handleReset = () => {
    // Clean up URLs before resetting state
    images.forEach(image => {
      if (image.preview) URL.revokeObjectURL(image.preview);
    });
    
    optimizedImages.forEach(image => {
      if (image?.preview) URL.revokeObjectURL(image.preview);
    });
    
    setImages([]);
    setOptimizedImages([]);
    setLoadingImages([]);
    setSelectedImageIndex(null);
  };

  const handleImageSelect = (index) => {
    setSelectedImageIndex(prevIndex => prevIndex === index ? null : index);
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      images.forEach(image => {
        if (image.preview) URL.revokeObjectURL(image.preview);
      });
      optimizedImages.forEach(image => {
        if (image?.preview) URL.revokeObjectURL(image.preview);
      });
    };
  }, []);

  return (
    <div className="py-8 px-4 bg-[#1A1A1A] text-white min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-center">Optimizador de Imágenes</h1>
      
      {/* Drag & Drop Area */}
      <div 
        {...getRootProps()} 
        className={`p-8 text-center cursor-pointer border-2 border-dashed rounded-lg mb-6 transition-colors duration-300 ${
          isDragActive 
            ? 'border-[#ff1b6d] bg-[#ff1b6d]/10' 
            : 'border-gray-600 hover:border-[#ff1b6d]/80 hover:bg-[#2a2a2a]'
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center">
          <FiUploadCloud className="text-5xl text-[#ff1b6d] mb-3" />
          <h3 className="text-xl font-medium mb-2">
            Arrastra y suelta imágenes aquí, o haz clic para seleccionar
          </h3>
          <p className="text-gray-400">
            Soporta JPEG, PNG, WebP y GIF (Se permiten múltiples archivos)
          </p>
        </div>
      </div>

      {images.length > 0 && (
        <>
          {/* Settings Panel */}
          <div className="bg-[#2a2a2a] rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Ajustes de Optimización</h2>
            
            {/* Max Width */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Ancho Máximo: {settings.maxWidth}px
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs">100</span>
                <input 
                  type="range" 
                  min="100" 
                  max="3840" 
                  step="100"
                  value={settings.maxWidth}
                  onChange={(e) => handleSettingsChange('maxWidth', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#ff1b6d]"
                />
                <span className="text-xs">3840</span>
              </div>
            </div>
            
            {/* Quality */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Calidad: {settings.quality}%
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs">1</span>
                <input 
                  type="range" 
                  min="1" 
                  max="100" 
                  value={settings.quality}
                  onChange={(e) => handleSettingsChange('quality', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#ff1b6d]"
                />
                <span className="text-xs">100</span>
              </div>
            </div>
            
            {/* Maintain Aspect Ratio */}
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.maintainAspectRatio}
                  onChange={(e) => handleSettingsChange('maintainAspectRatio', e.target.checked)}
                  className="mr-2 h-4 w-4 rounded border-gray-300 text-[#ff1b6d] focus:ring-[#ff1b6d]"
                />
                <span className="text-sm font-medium">Mantener Proporción</span>
              </label>
            </div>
            
            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Formato de Salida</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="format"
                    value="jpeg"
                    checked={settings.format === 'jpeg'}
                    onChange={() => handleSettingsChange('format', 'jpeg')}
                    className="mr-2 h-4 w-4 border-gray-300 text-[#ff1b6d] focus:ring-[#ff1b6d]"
                  />
                  <span className="text-sm">JPEG</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="format"
                    value="png"
                    checked={settings.format === 'png'}
                    onChange={() => handleSettingsChange('format', 'png')}
                    className="mr-2 h-4 w-4 border-gray-300 text-[#ff1b6d] focus:ring-[#ff1b6d]"
                  />
                  <span className="text-sm">PNG</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="format"
                    value="webp"
                    checked={settings.format === 'webp'}
                    onChange={() => handleSettingsChange('format', 'webp')}
                    className="mr-2 h-4 w-4 border-gray-300 text-[#ff1b6d] focus:ring-[#ff1b6d]"
                  />
                  <span className="text-sm">WebP</span>
                </label>
              </div>
            </div>
          </div>
          
          {/* Actions Bar */}
          <div className="mb-6 flex flex-wrap justify-between items-center gap-3">
            <h2 className="text-xl font-semibold">Imágenes Cargadas ({images.length})</h2>
            <div className="flex flex-wrap gap-2">
              <button 
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  loading || images.length === 0 
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                    : 'bg-[#ff1b6d] text-white hover:bg-[#ff1b6d]/90'
                }`}
                onClick={optimizeAllImages}
                disabled={loading || images.length === 0}
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Optimizando...
                  </div>
                ) : 'Optimizar Todas las Imágenes'}
              </button>
              <button
                className={`px-4 py-2 rounded-full font-medium border transition-colors ${
                  loading 
                    ? 'border-gray-700 text-gray-400 cursor-not-allowed' 
                    : 'border-[#ff1b6d] text-[#ff1b6d] hover:bg-[#ff1b6d]/10'
                }`}
                onClick={handleReset}
                disabled={loading}
              >
                <span className="flex items-center">
                  <FiTrash2 className="mr-1" />
                  Reiniciar Todo
                </span>
              </button>
            </div>
          </div>
          
          {/* Images Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {images.map((image, index) => (
              <div 
                key={image.id || index}
                className={`bg-[#2a2a2a] rounded-lg overflow-hidden transition-shadow hover:shadow-lg ${
                  selectedImageIndex === index ? 'ring-2 ring-[#ff1b6d]' : ''
                }`}
                onClick={() => handleImageSelect(index)}
              >
                <div className="aspect-video bg-[#1e1e1e] flex items-center justify-center overflow-hidden">
                  <img 
                    src={image.preview} 
                    alt={`Imagen ${index + 1}`}
                    className="max-h-full object-contain"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-lg mb-1 truncate" title={image.name}>
                    {image.name}
                  </h3>
                  <p className="text-sm text-gray-400 mb-1">
                    Original: {(image.size / 1024).toFixed(2)} KB
                  </p>
                  {optimizedImages[index] && (
                    <p className="text-sm text-green-500">
                      Optimizado: {(optimizedImages[index].size / 1024).toFixed(2)} KB
                      {image.size > 0 && (
                        <span> ({(100 - (optimizedImages[index].size / image.size) * 100).toFixed(0)}% más pequeño)</span>
                      )}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button 
                      className={`text-sm px-3 py-1 rounded-full transition-colors ${
                        loadingImages[index] || loading 
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                          : 'bg-[#1e1e1e] text-white hover:bg-[#ff1b6d] hover:text-white'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        optimizeImageHandler(index);
                      }}
                      disabled={loadingImages[index] || loading}
                    >
                      {loadingImages[index] ? (
                        <div className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Optimizando
                        </div>
                      ) : 'Optimizar'}
                    </button>
                    {optimizedImages[index] && (
                      <button 
                        className="text-sm px-3 py-1 bg-[#1e1e1e] text-white rounded-full hover:bg-[#ff1b6d] transition-colors flex items-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(index);
                        }}
                        disabled={loadingImages[index]}
                      >
                        <FiDownload className="mr-1" />
                        Descargar
                      </button>
                    )}
                    <button 
                      className="text-sm px-3 py-1 bg-[#1e1e1e] text-white rounded-full hover:bg-red-600 transition-colors flex items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage(index);
                      }}
                      disabled={loadingImages[index]}
                    >
                      <FiTrash2 className="mr-1" />
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Download All Section */}
          {optimizedImages.some(img => img !== null) && (
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <button
                className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center ${
                  loading 
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                    : 'bg-[#ff1b6d] text-white hover:bg-[#ff1b6d]/90'
                }`}
                onClick={handleDownloadAll}
                disabled={loading}
              >
                <FiDownload className="mr-2" />
                Descargar Todas Individualmente
              </button>
              <button
                className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center ${
                  loading 
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
                onClick={handleDownloadAllAsZip}
                disabled={loading}
              >
                <FiDownload className="mr-2" />
                Descargar Como ZIP
              </button>
            </div>
          )}
        </>
      )}

      {/* Error Notification */}
      {error && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 w-full max-w-md bg-red-700 text-white p-4 rounded-lg shadow-lg">
          <div className="flex items-start">
            <div className="flex-1">
              <h3 className="font-medium">Error</h3>
              <p className="text-sm mt-1">{error}</p>
            </div>
            <button 
              onClick={handleCloseError}
              className="text-white hover:text-gray-200"
            >
              &times;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageOptimizer;
